import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  useRideEmptyStationsAllowed,
  useRideStationContext,
} from "@ryde/redux";
import {
  isRideGreyed,
  isStationRouteWaypointStation,
  NO_VALUE_DISPLAY,
  RideStationPanelMode,
  useFormats,
  useSystemParams,
} from "@ryde/shared";

import { FramerTooltip } from "../../../tooltips";
import { RideStationTimeEdit } from "./ride-station-time-edit";
import {
  RideStationPlannedActualTimeStyled,
  StationBoxRightStyled,
} from "./ride-stations.styled";

export const RideStationTimeCell: FC = () => {
  const { mode, station, rideDto } = useRideStationContext();
  const { formatTimeNormalized } = useFormats();
  const { t } = useTranslation();
  const { actualTimeAtStationRideEdit } = useSystemParams();
  const { isRideHasIndependentDayAdjustment } = useRideEmptyStationsAllowed(
    mode,
    rideDto,
  );
  const { arrivalTime, realArrivalTime } = station || {};
  const isGreyed = isRideGreyed(rideDto);
  const isWaypoint = isStationRouteWaypointStation(station);

  const timeEditAllowed = useMemo(() => {
    if (isWaypoint) {
      return false;
    }

    if (isRideHasIndependentDayAdjustment) {
      return false;
    }

    if (mode === RideStationPanelMode.DRAG_OVERLAY) {
      return false;
    }

    if (isGreyed) {
      return false;
    }

    return true;
  }, [isGreyed, isRideHasIndependentDayAdjustment, isWaypoint, mode]);

  if (isRideHasIndependentDayAdjustment) {
    return <StationBoxRightStyled>{NO_VALUE_DISPLAY}</StationBoxRightStyled>;
  }

  // show planned (light) / actual GPS arrival (dominant) side by side, read-only.
  // Actual is "--" when there is no GPS-based arrival for this station.
  if (actualTimeAtStationRideEdit) {
    return (
      <StationBoxRightStyled>
        <RideStationPlannedActualTimeStyled>
          <FramerTooltip content={t("ride.INDICATORS.plannedTimeTooltip")}>
            <span className="planned-time">
              {arrivalTime
                ? formatTimeNormalized(arrivalTime)
                : NO_VALUE_DISPLAY}
            </span>
          </FramerTooltip>
          <span className="time-separator">/</span>
          <FramerTooltip content={t("ride.INDICATORS.actualTimeTooltip")}>
            <span className="actual-time">
              {realArrivalTime
                ? formatTimeNormalized(realArrivalTime)
                : NO_VALUE_DISPLAY}
            </span>
          </FramerTooltip>
        </RideStationPlannedActualTimeStyled>
      </StationBoxRightStyled>
    );
  }

  if (timeEditAllowed) {
    return <RideStationTimeEdit />;
  }

  return (
    <StationBoxRightStyled>
      {arrivalTime ? formatTimeNormalized(arrivalTime) : NO_VALUE_DISPLAY}
    </StationBoxRightStyled>
  );
};
